import CoachNextSessionPanel from './CoachNextSessionPanel';
import CoachOpsRail from './CoachOpsRail';
import CoachStudioSetupPanel from './CoachStudioSetupPanel';
import CoachTodayTimeline from './CoachTodayTimeline';
import type { CoachDailyCommandBoardProps } from './coachStudioTypes';
import { isToday } from './coachStudioUtils';

export default function CoachDailyCommandBoard({
  workTabLabel,
  directoryLabel: _directoryLabel,
  accessLabel: _accessLabel,
  occurrences,
  programCount,
  coachDashboard,
  nextSessionDetail,
  nextSessionSlotFillers,
  isNextSessionLoading,
  activeCount: _activeCount,
  linkedAthleteCount,
  pendingInvitationCount,
  activeJoinCodeCount,
  provider: _provider,
  providerViews: _providerViews,
  providerContacts: _providerContacts,
  onOpenCatalog,
  onOpenWork,
  onOpenDirectory,
  onOpenAccess,
  onManagePros: _onManagePros,
  onNavigate,
}: CoachDailyCommandBoardProps) {
  const todayOccurrences = occurrences.filter((occurrence) => isToday(occurrence.startsAt));
  const boardOccurrences = todayOccurrences.length > 0 ? todayOccurrences : occurrences.slice(0, 4);
  const nextOccurrence = nextSessionDetail?.occurrence ?? occurrences[0] ?? null;
  const rosterSummary = nextSessionDetail?.rosterSummary ?? null;
  const attendanceSummary = nextSessionDetail?.attendanceSummary ?? null;
  const makeupSummary = nextSessionDetail?.makeupSummary ?? null;
  const slotFillerCount = nextSessionSlotFillers?.suggestions.length ?? 0;
  const openSpotCount =
    nextSessionSlotFillers?.openSpotCount
    ?? rosterSummary?.openSpots
    ?? (rosterSummary?.capacity != null ? Math.max(rosterSummary.capacity - rosterSummary.bookedCount, 0) : null);
  const attentionItems = coachDashboard?.attentionItems ?? [];
  const bookingRequests = coachDashboard?.bookingRequests ?? [];
  const moneySummary = coachDashboard?.money ?? null;
  const hasMoneySignal = Boolean(
    (moneySummary?.amountDue ?? 0) > 0
      || (moneySummary?.paidAmount ?? 0) > 0
      || (moneySummary?.balanceAmount ?? 0) > 0
      || (moneySummary?.refundedAmount ?? 0) > 0
  );
  const hasSessions = occurrences.length > 0;
  const hasStudents = linkedAthleteCount > 0;
  const showOpsRail = (attentionItems.length > 0 && (hasSessions || hasStudents)) || bookingRequests.length > 0 || hasMoneySignal;

  return (
    <div className={showOpsRail ? 'grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]' : 'space-y-6'}>
      <div className="min-w-0 space-y-6">
        {nextOccurrence ? (
          <>
            <CoachNextSessionPanel
              occurrence={nextOccurrence}
              rosterSummary={rosterSummary}
              attendanceSummary={attendanceSummary}
              makeupSummary={makeupSummary}
              openSpotCount={openSpotCount}
              slotFillerCount={slotFillerCount}
              isLoading={isNextSessionLoading}
              onOpenWork={onOpenWork}
            />

            <CoachTodayTimeline
              occurrences={boardOccurrences}
              totalTodayCount={todayOccurrences.length}
              workTabLabel={workTabLabel}
              onOpenWork={onOpenWork}
            />
          </>
        ) : (
          <CoachStudioSetupPanel
            linkedAthleteCount={linkedAthleteCount}
            activeJoinCodeCount={activeJoinCodeCount}
            pendingInvitationCount={pendingInvitationCount}
            programCount={programCount}
            onOpenCatalog={onOpenCatalog}
            onOpenDirectory={onOpenDirectory}
            onOpenAccess={onOpenAccess}
            onOpenWork={onOpenWork}
          />
        )}
      </div>

      {showOpsRail ? (
        <aside className="min-w-0 xl:sticky xl:top-8 xl:self-start">
          <CoachOpsRail
            attentionItems={attentionItems}
            bookingRequests={bookingRequests}
            money={moneySummary}
            onNavigate={onNavigate}
            onOpenWork={onOpenWork}
            onOpenCatalog={onOpenCatalog}
          />
        </aside>
      ) : null}
    </div>
  );
}
